import { Component, ElementRef, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar, MatSnackBarRef, TextOnlySnackBar } from '@angular/material/snack-bar';
import { WorkflowStep } from '@app/models/workflow-step';
import { OrchestratorService } from '@app/services/orchestrator.service';
import { StepperService } from '@app/services/stepper.service';
import { StoreService } from '@app/services/store.service';
import { first, firstValueFrom, map, Observable, Subject, takeUntil } from 'rxjs';

@Component({
	selector: 'mv-other-names',
	templateUrl: './other-names.component.html',
	styleUrls: ['./other-names.component.scss'],
})
export class OtherNamesComponent implements OnInit, OnDestroy {
	@Output() _emb_close: EventEmitter<boolean> = new EventEmitter<boolean>();
	public nameForm: FormGroup;
	public hideNameInput: boolean = true;
	public petName$: Observable<string> = this.storeService.appState$.pipe(map(s => s.petName ?? ''));
	public clinicName$: Observable<string> = this.storeService.formState$.pipe(
		map(s => {
			if (s.vetClinic?.accountName) return s.vetClinic?.accountName;
			else if (s.newVetClinic?.name) return s.newVetClinic?.name;
			else return '';
		})
	);
	private readonly workflowStep: number = 3;
	private snackBarRef?: MatSnackBarRef<TextOnlySnackBar>;
	private readonly ngUnsubscribe$: Subject<boolean> = new Subject();
	private readonly maxInputLength: number = 100;

	constructor(
		public readonly el: ElementRef<HTMLElement>,
		private readonly fb: FormBuilder,
		private readonly orchestratorService: OrchestratorService,
		private readonly snackbar: MatSnackBar,
		private readonly storeService: StoreService,
		private readonly stepperService: StepperService
	) {
		this.nameForm = this.fb.group({
			hasOtherNames: [null, Validators.required],
			otherNames: [null, Validators.maxLength(this.maxInputLength)],
		});

		this.storeService.formState$.pipe(first()).subscribe(({ hasOtherNames, otherNamesValue: otherNames }) => {
			this.hideNameInput = !hasOtherNames;
			this.nameForm.patchValue({
				hasOtherNames,
				otherNames,
			});
		});
	}

	private get hasOtherNamesControl(): AbstractControl | null {
		return this.nameForm.get('hasOtherNames');
	}

	private get otherNamesControl(): AbstractControl | null {
		return this.nameForm.get('otherNames');
	}

	ngOnInit(): void {
		this.stepperService.updateStep(this.workflowStep);

		this.hasOtherNamesControl?.valueChanges.pipe(takeUntil(this.ngUnsubscribe$)).subscribe(value => {
			if (!value) {
				this.hideNameInput = true;
				this.otherNamesControl?.reset();
				this.otherNamesControl?.clearValidators();
			}

			if (value) {
				this.otherNamesControl?.setValidators([Validators.required]);
				this.hideNameInput = false;
			}

			this.otherNamesControl?.updateValueAndValidity();
		});
	}

	// eslint-disable-next-line @typescript-eslint/require-await
	public async nextStep(): Promise<void> {
		if (this.nameForm.invalid) return;

		const { hasOtherNames, otherNames: otherNamesValue } = this.nameForm.getRawValue();
		this.storeService.updateFormState({
			hasOtherNames,
			otherNamesValue,
		});
		try {
			const petId = await firstValueFrom(this.storeService.appState$.pipe(map(s => s.petId ?? '')));
			const formValues = await firstValueFrom(this.storeService.formState$);
			if (formValues.cannotFindVet) {
				await firstValueFrom(
					this.orchestratorService.addVet(petId, {
						cityState: this.getCityState(formValues.newVetClinic.address),
						name: formValues.newVetClinic.name,
						otherOwners: formValues.otherOwnersValue ?? null,
						otherNames: otherNamesValue ?? null,
						unknownProviderAccount: {
							name: formValues.newVetClinic.name,
							address: formValues.newVetClinic.address,
							zipCode: formValues.newVetClinic.zipCode,
							state: formValues.newVetClinic.state,
							phoneNumber: formValues.newVetClinic.phoneNumber ?? null,
						},
					})
				);
			} else {
				await firstValueFrom(
					this.orchestratorService.addVet(petId, {
						cityState: this.getCityState(formValues.vetClinic.fullAddress),
						name: formValues.vetClinic.accountName,
						vetId: formValues.vetClinic.accountId,
						otherOwners: formValues.otherOwnersValue ?? null,
						otherNames: otherNamesValue ?? null,
					})
				);
			}
			// FIXME: Update to new pattern
			this.storeService.updateWorkflowStep(WorkflowStep.Celebration);
		} catch (err) {
			console.error(err);
			this.snackBarRef = this.snackbar.open('There was an error adding this vet. Please try again later.', 'Dismiss', {
				panelClass: ['addition__failure', 'm--warn'],
			});
		}
	}

	public stepBack(): void {
		this.storeService.updateWorkflowStep(WorkflowStep.OtherOwners);
	}

	public cancel(): void {
		this._emb_close.emit(false);
		this.storeService.cleanupFormState();
	}

	private getCityState(fullAddress: string): string {
		return fullAddress
			.split(', ')
			.slice(1)
			.map(e => e.replace(/\s\d+/, ''))
			.join(', ');
	}

	// eslint-disable-next-line @typescript-eslint/member-ordering
	ngOnDestroy(): void {
		this.ngUnsubscribe$.next(true);
		this.ngUnsubscribe$.unsubscribe();
	}
}
